import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import { Skeleton } from '../components/ui/Skeleton'
import { isPersonalMode } from '../utils/cockpitMode'
import { getRuntimeConfig } from '../lib/runtimeConfig'
import AriaSettings from '../components/AriaSettings/AriaSettings'
import MySoulEditor from '../components/MySoulEditor'
import AriaOrgSettings from './AriaOrgSettings'
import OrgAiSettingsTab from './settings/OrgAiSettingsTab'
import DeleteAccountModal from '../components/DeleteAccountModal'
import ComingSoon from '../components/ComingSoon'
import { isComingSoon } from '../lib/comingSoon'
import './Settings.css'
import { scopedLocalStorage } from '../lib/storage'
import { displayIdentity, isItsceServiceAccount } from '../utils/identityDisplay'

// TQ #6931 Refactor Wave 3: Personal-Mode-Konstanten (KC-Account-Console-Link).
const PERSONAL_KC_REALM = 'Personal-Users'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface OrgDetails {
  id: number
  name: string
  legal_name?: string
  slug: string
  vat_id?: string
  description?: string
  contact_email?: string
  contact_phone?: string
  keycloak_realm?: string
  realm_status?: string
  created_at?: string
  needs_onboarding?: boolean
  member_role?: string
}

interface Props {
  organization: Organization
}

// TQ #8504: Einstellungen ist jetzt EIN Bereich mit Tab-Leiste (Hub-Muster:
// Einstellungen → Tabs), statt eines Zahnrad-Icons auf der Aria-Seite. Die
// Aria-Sub-Tabs (KI-Einstellungen via geteilter @it-sce/aria-settings-form,
// Persona, Org-Defaults, Datenschutz, GroupFolders, Audit) leben hier, NICHT
// mehr hinter dem Zahnrad in CortexPage (das Zahnrad ist entfernt). Die Aria-
// Seite ist wieder reiner Chat.
type SettingsTab = 'organisation' | 'soul' | 'aria' | 'datenschutz' | 'audit'

// TQ #491 Phase 2 (Patric 03.10.2026): 8 Tabs → 5. Alte Tab-IDs bleiben als Deep-Link gueltig und
// werden auf den neuen Tab plus Abschnitts-Anker umgeleitet (z. B. ?tab=ki → aria#ki-modus).
const LEGACY_TAB_REDIRECTS: Readonly<Record<string, { tab: SettingsTab; anchor?: string }>> = {
  ki: { tab: 'aria', anchor: 'ki-modus' },
  persona: { tab: 'aria', anchor: 'persona' },
  defaults: { tab: 'aria', anchor: 'org-defaults' },
  groupfolders: { tab: 'datenschutz', anchor: 'groupfolders' },
}

const TAB_DEFS: ReadonlyArray<{
  id: SettingsTab
  label: string
  adminOnly: boolean
  personalOnly?: boolean
  // TQ #11415: Personal-Modus (buildPersonalPseudoOrg) setzt member_role='owner' als Platzhalter
  // fuer eine Organisation, die es nicht gibt. orgOnly markiert Tabs, die nur fuer eine echte
  // Organisation Sinn ergeben und im Personal-Modus unabhaengig von adminOnly ausgeblendet werden.
  orgOnly?: boolean
}> = [
  { id: 'organisation', label: 'Organisation', adminOnly: false },
  { id: 'soul', label: 'Mein Charakter', adminOnly: false, personalOnly: true },
  // Fuer alle sichtbar: persoenliche Modell-Auswahl und eigene Zugaenge; die Org-Abschnitte
  // (KI-Modus, Persona, Org-Defaults) zeigt der Tab nur Admin/Owner im Org-Modus.
  { id: 'aria', label: 'Aria', adminOnly: false },
  // TQ #8459/#10086: fuer ALLE Mitglieder sichtbar (Lesezugriff); Aendern bleibt admin/owner-only.
  // Bewusst KEIN orgOnly (TQ #11415): PrivacyModeSelector bleibt im Personal-Modus sichtbar.
  { id: 'datenschutz', label: 'Datenschutz & Freigaben', adminOnly: false },
  { id: 'audit', label: 'Audit-Log', adminOnly: true, orgOnly: true },
]

function isSettingsTab(s: string | null): s is SettingsTab {
  if (!s) return false
  return TAB_DEFS.some((t) => t.id === s)
}

// ─── Organisation/Profil-Tab (ex-Settings-Body) ─────────────────────────────

function OrgProfileTab({ organization }: Props) {
  const { showToast } = useToast()
  const { user, logout } = useAuth()
  // TQ #6931: Personal-Mode (with.it-sce.de) hat keine Org → Profil aus KC-JWT +
  // Aria-Persona (localStorage) statt Org-Profile-Edit-Form (conditional hide).
  const personal = isPersonalMode()
  const [details, setDetails] = useState<OrgDetails | null>(null)
  const [loading, setLoading] = useState(!isPersonalMode())
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  // TQ #6963: KI-Persona ist jetzt server-synced (/api/me/persona). localStorage
  // bleibt nur als Offline-Cache fuer instant paint, kein Source-of-Truth mehr.
  const [persona, setPersona] = useState<string>(scopedLocalStorage.getItem('cockpit_persona') || '')
  const [personaSaved, setPersonaSaved] = useState(false)
  const [personaSaving, setPersonaSaving] = useState(false)
  useEffect(() => {
    if (!personal) return
    let cancelled = false
    api.getMyPersona()
      .then(r => {
        if (cancelled || !r?.success) return
        setPersona(r.persona || '')
        scopedLocalStorage.setItem('cockpit_persona', r.persona || '')
      })
      .catch(() => { /* offline → localStorage-Cache bleibt sichtbar */ })
    return () => { cancelled = true }
  }, [personal])
  const savePersona = async () => {
    setPersonaSaving(true)
    try {
      await api.updateMyPersona(persona)
      scopedLocalStorage.setItem('cockpit_persona', persona)
      setPersonaSaved(true)
      setTimeout(() => setPersonaSaved(false), 2000)
    } catch {
      showToast('error', 'Persona konnte nicht gespeichert werden')
    } finally {
      setPersonaSaving(false)
    }
  }

  // TQ #7646: Per-User-Rechnungsadresse (Personal-Mode). Wird via
  // PUT /api/me/address in die ERPNext-Billing-Address gespiegelt.
  const [address, setAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
  })
  const [addressSaving, setAddressSaving] = useState(false)
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress({ ...address, [e.target.name]: e.target.value })
  }
  const saveAddress = async () => {
    setAddressSaving(true)
    try {
      await api.updateMyAddress(address)
      showToast('success', 'Adresse wurde gespeichert')
    } catch {
      showToast('error', 'Adresse konnte nicht gespeichert werden')
    } finally {
      setAddressSaving(false)
    }
  }

  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
  })

  const [originalData, setOriginalData] = useState({
    name: '',
    legal_name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
  })

  useEffect(() => {
    // Personal-Mode: kein /api/org/<slug>-Endpoint → Org-Daten nicht laden.
    if (personal) return
    loadDetails()
  }, [personal, organization.slug])

  useEffect(() => {
    // Check if form data differs from original
    const changed = Object.keys(formData).some(
      key => formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
    )
    setHasChanges(changed)
  }, [formData, originalData])

  const loadDetails = async () => {
    try {
      const response = await api.getOrganization((organization.public_token || organization.slug))
      setDetails(response.organization)
      const data = {
        name: response.organization.name || '',
        legal_name: response.organization.legal_name || '',
        description: response.organization.description || '',
        contact_email: response.organization.contact_email || '',
        contact_phone: response.organization.contact_phone || '',
      }
      setFormData(data)
      setOriginalData(data)
    } catch (error) {
      console.error('Failed to load organization:', error)
      showToast('error', 'Organisationsdaten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSave = async () => {
    setSaving(true)

    try {
      await api.updateOrganization((organization.public_token || organization.slug), formData)
      setOriginalData({ ...formData })
      showToast('success', 'Einstellungen wurden gespeichert')
    } catch (error) {
      console.error('Failed to save:', error)
      showToast('error', 'Fehler beim Speichern der Einstellungen')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setFormData({ ...originalData })
  }

  const getRealmStatusLabel = (status: string | undefined) => {
    switch (status) {
      case 'active': return 'Aktiv'
      case 'pending': return 'In Einrichtung'
      case 'disabled': return 'Deaktiviert'
      default: return status || '-'
    }
  }

  if (!personal && loading) {
    return (
      <div className="settings-container">
        <section className="settings-section">
          <Skeleton width="40%" height={24} />
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Skeleton width="100%" height={44} borderRadius={10} />
            <Skeleton width="100%" height={44} borderRadius={10} />
            <Skeleton width="100%" height={80} borderRadius={10} />
          </div>
        </section>
      </div>
    )
  }

  if (personal) {
    return (
      <div>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>Profil</h2>
        <div className="service-card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: '0.75rem', columnGap: '1rem', fontSize: '0.95rem' }}>
            <div style={{ color: '#6b7280' }}>Name</div>
            <div>{user?.firstName || user?.lastName ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() : '—'}</div>
            {!isItsceServiceAccount(user) && (
              <>
                <div style={{ color: '#6b7280' }}>E-Mail</div>
                <div>{user?.email || '—'}</div>
              </>
            )}
            <div style={{ color: '#6b7280' }}>Benutzername</div>
            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}>{(isItsceServiceAccount(user) ? displayIdentity(user) : user?.username || user?.email) || '—'}</div>
          </div>
          <a
            href={`${getRuntimeConfig().keycloakUrl}/realms/${PERSONAL_KC_REALM}/account`}
            target="_blank" rel="noopener noreferrer"
            className="btn-primary"
            style={{ marginTop: '1rem', textDecoration: 'none', display: 'inline-block' }}
          >Account-Console öffnen ↗</a>
        </div>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>KI-Persona (Aria)</h2>
        <div className="service-card" style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0 0 1rem' }}>
            Was soll Aria über dich wissen? Branche, Rolle, Vorlieben, Tonalität.
            Aria nutzt das als Kontext für alle Antworten.
          </p>
          <textarea
            value={persona}
            onChange={e => setPersona(e.target.value)}
            rows={6}
            placeholder={"Beispiel:\n• Ich bin Architekt, viel unterwegs.\n• Antworte präzise und ohne Floskeln.\n• Termine immer Mo–Fr 9:00–18:00."}
            style={{
              width: '100%', padding: '0.75rem', borderRadius: 8,
              border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
              resize: 'vertical', minHeight: 120,
            }}
          />
          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={savePersona} className="btn-primary" disabled={personaSaving}>
              {personaSaving ? 'Wird gespeichert…' : 'Speichern'}
            </button>
            {personaSaved && <span style={{ color: '#065f46', fontSize: '0.85rem' }}>✓ Gespeichert</span>}
            <span style={{ fontSize: '0.8rem', color: '#9ca3af', marginLeft: 'auto' }}>
              Wird serverseitig gespeichert und für Aria verwendet.
            </span>
          </div>
        </div>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>Rechnungsadresse</h2>
        <div className="service-card" style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0 0 1rem' }}>
            Deine Rechnungsadresse für Belege und Abrechnungen.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label
                htmlFor="address_line1"
                style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}
              >Straße und Hausnummer</label>
              <input
                id="address_line1"
                type="text"
                name="address_line1"
                value={address.address_line1}
                onChange={handleAddressChange}
                placeholder="z.B. Musterstraße 12"
                style={{
                  width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                  border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
                }}
              />
            </div>
            <div>
              <label
                htmlFor="address_line2"
                style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}
              >Adresszusatz (optional)</label>
              <input
                id="address_line2"
                type="text"
                name="address_line2"
                value={address.address_line2}
                onChange={handleAddressChange}
                placeholder="z.B. c/o, Gebäude, Etage"
                style={{
                  width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                  border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ flex: '0 0 30%' }}>
                <label
                  htmlFor="pincode"
                  style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}
                >PLZ</label>
                <input
                  id="pincode"
                  type="text"
                  name="pincode"
                  value={address.pincode}
                  onChange={handleAddressChange}
                  placeholder="74523"
                  style={{
                    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
                  }}
                />
              </div>
              <div style={{ flex: '1 1 auto' }}>
                <label
                  htmlFor="city"
                  style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}
                >Stadt</label>
                <input
                  id="city"
                  type="text"
                  name="city"
                  value={address.city}
                  onChange={handleAddressChange}
                  placeholder="Schwäbisch Hall"
                  style={{
                    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <button onClick={saveAddress} className="btn-primary" disabled={addressSaving}>
              {addressSaving ? 'Wird gespeichert…' : 'Adresse speichern'}
            </button>
          </div>
        </div>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>Daten</h2>
        <div className="service-card" style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0 0 1rem' }}>
            DSGVO-Auskunft und Datenexport über den Support.
          </p>
          <a
            href="mailto:datenschutz@it-sce.de?subject=DSGVO-Anfrage&body=Bitte%20gebt%20mir%20Auskunft%20zu%20meinen%20gespeicherten%20Daten."
            className="btn-primary"
            style={{ textDecoration: 'none', display: 'inline-block' }}
          >DSGVO-Anfrage stellen</a>
        </div>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>Konto löschen</h2>
        <div className="service-card">
          <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0 0 1rem' }}>
            Löscht dein Konto unwiderruflich (nach 30 Tagen Karenzzeit). Du musst dein
            aktuelles Passwort zur Bestätigung eingeben.
          </p>
          <DeleteAccountModal
            requiresPassword
            onDeleted={() => {
              setTimeout(() => logout(), 1500)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="settings-container">
      <section className="settings-section">
        <div className="section-header">
          <h2>Allgemeine Informationen</h2>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Organisationsname</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="z.B. Musterfirma GmbH"
            />
          </div>
          <div className="form-group">
            <label htmlFor="legal_name">Rechtlicher Name</label>
            <input
              id="legal_name"
              type="text"
              name="legal_name"
              value={formData.legal_name}
              onChange={handleChange}
              placeholder="z.B. Musterfirma Gesellschaft mbH"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Beschreibung</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Kurze Beschreibung der Organisation..."
          />
        </div>
      </section>

      <section className="settings-section">
        <div className="section-header">
          <h2>Kontaktdaten</h2>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="contact_email">Kontakt E-Mail</label>
            <input
              id="contact_email"
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              placeholder="kontakt@beispiel.de"
            />
          </div>
          <div className="form-group">
            <label htmlFor="contact_phone">Kontakt Telefon</label>
            <input
              id="contact_phone"
              type="tel"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              placeholder="+49 123 456789"
            />
          </div>
        </div>
      </section>

      {(organization.member_role === 'owner' || organization.member_role === 'admin') && (
        <section className="settings-section">
          <div className="section-header">
            <h2>Onboarding-Wizard</h2>
            <span className="section-badge">{details?.needs_onboarding ? 'Offen' : 'Abgeschlossen'}</span>
          </div>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary, #666)' }}>
            {details?.needs_onboarding
              ? 'Beim nächsten Login eines Org-Admins wird der Scout-Wizard angezeigt, um die Aria-Konfiguration zu vervollständigen.'
              : 'Der Wizard wurde durchlaufen. Du kannst ihn jederzeit neu starten, wenn sich der Geschäftskontext geändert hat (neue Branche, neue Tools, neue Personen).'}
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              try {
                await api.restartOrgWizard(organization.slug)
                showToast('success', 'Wizard wurde zurückgesetzt — beim nächsten Login startet er neu.')
                await loadDetails()
              } catch (err) {
                console.error('[Settings] restartOrgWizard failed:', err)
                showToast('error', 'Wizard konnte nicht zurückgesetzt werden.')
              }
            }}
          >
            Onboarding-Wizard neu starten
          </button>
        </section>
      )}

      {organization.member_role === 'owner' && (
        <section className="settings-section">
          <div className="section-header">
            <h2>Konto löschen</h2>
          </div>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary, #666)' }}>
            Als Eigentümer:in dieser Organisation kannst du dein Konto nicht direkt
            löschen — unabhängig davon, ob die Organisation weitere Mitglieder oder
            aktive Services hat. Wende dich an den Support (datenschutz@it-sce.de),
            um die Eigentümerschaft zu übertragen oder die Organisation aufzulösen.
          </p>
          <DeleteAccountModal
            requiresPassword={false}
            onDeleted={() => {
              setTimeout(() => logout(), 1500)
            }}
          />
        </section>
      )}

      <section className="settings-section readonly-section">
        <div className="section-header">
          <h2>Technische Details</h2>
          <span className="section-badge readonly">Nur Lesen</span>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Slug / Kennung</span>
            <span className="info-value">
              <code>{details?.slug}</code>
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">USt-IdNr.</span>
            <span className="info-value">{details?.vat_id || 'Nicht hinterlegt'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Keycloak Realm</span>
            <span className="info-value">
              <code>{details?.keycloak_realm || '-'}</code>
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Realm Status</span>
            <span className={`info-value status-value status-${details?.realm_status}`}>
              {getRealmStatusLabel(details?.realm_status)}
            </span>
          </div>
        </div>
      </section>

      <div className="settings-actions">
        {hasChanges && (
          <button className="btn-secondary" onClick={handleReset}>
            Änderungen verwerfen
          </button>
        )}
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
        </button>
      </div>
    </div>
  )
}

// ─── Tabbar-Host (TQ #8504) ─────────────────────────────────────────────────

export default function Settings({ organization }: Props) {
  const personal = isPersonalMode()
  const isAdminOrOwner =
    organization.member_role === 'owner' || organization.member_role === 'admin'

  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const { pathname } = useLocation()

  const visibleTabs = useMemo(
    () =>
      TAB_DEFS.filter(
        (t) =>
          (!t.adminOnly || isAdminOrOwner) &&
          (!t.personalOnly || personal) &&
          (!t.orgOnly || !personal),
      ),
    [isAdminOrOwner, personal],
  )

  // Alte Tab-IDs (?tab=ki, persona, defaults, groupfolders, mine) auf den neuen Tab + Anker abbilden.
  const legacy = requestedTab ? LEGACY_TAB_REDIRECTS[requestedTab] : undefined
  const resolvedTab = legacy ? legacy.tab : requestedTab
  const anchor = legacy?.anchor

  const activeTab: SettingsTab = useMemo(() => {
    if (isSettingsTab(resolvedTab) && visibleTabs.some((t) => t.id === resolvedTab)) {
      return resolvedTab
    }
    return 'organisation'
  }, [resolvedTab, visibleTabs])

  // URL auf die neue Form umschreiben und zum Abschnitt springen.
  useEffect(() => {
    if (!legacy) return
    const params = new URLSearchParams(searchParams)
    params.set('tab', legacy.tab)
    setSearchParams(params, { replace: true })
  }, [legacy, searchParams, setSearchParams])

  useEffect(() => {
    if (!anchor) return
    const t = window.setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => window.clearTimeout(t)
  }, [anchor, activeTab])

  const tabComingSoon = isComingSoon('settings') || isComingSoon(`settings:${activeTab}`)

  const selectTab = (t: SettingsTab) => {
    const params = new URLSearchParams(searchParams)
    if (t === 'organisation') params.delete('tab')
    else params.set('tab', t)
    setSearchParams(params, { replace: false })
  }

  // Das Token-Konto ist in die Abrechnung gezogen (Spec Informationsarchitektur, Patric 03.10.2026):
  // alte Links ?tab=tokens und ?tab=mine landen dort.
  if (requestedTab === 'tokens' || requestedTab === 'mine') {
    return <Navigate to={pathname.replace(/\/settings\/?$/, '/billing')} replace />
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Einstellungen</h1>
          <p className="page-subtitle">
            {personal
              ? 'Profil, KI-Persona und Aria-Einstellungen.'
              : 'Organisation, Aria-Konfiguration und Datenschutz.'}
          </p>
        </div>
      </div>

      <nav className="settings-tabbar" aria-label="Einstellungen">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`settings-tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => selectTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="settings-tab-content">
        {tabComingSoon ? (
          <ComingSoon feature={TAB_DEFS.find((t) => t.id === activeTab)?.label ?? 'Einstellungen'} />
        ) : (
          <>
          {activeTab === 'organisation' && <OrgProfileTab organization={organization} />}
          {activeTab === 'soul' && <MySoulEditor />}
          {activeTab === 'aria' && (
            <>
              {isAdminOrOwner && !personal && (
                <>
                  <div id="ki-modus"><OrgAiSettingsTab slug={organization.slug} /></div>
                  {/* EINE Instanz fuer Persona + Org-Defaults: ein State, ein Speichern-Knopf
                      (zwei Instanzen wuerden sich per PUT mit allen Feldern gegenseitig ueberschreiben). */}
                  <AriaOrgSettings
                    organization={organization}
                    sections={['persona', 'defaults']}
                    hideTabBar={true}
                  />
                </>
              )}
              <div id="meine-aria">
                <AriaSettings tenant={organization.slug} />
              </div>
            </>
          )}
          {activeTab === 'datenschutz' && (
            <AriaOrgSettings
              organization={organization}
              sections={isAdminOrOwner && !personal ? ['datenschutz', 'groupfolders'] : ['datenschutz']}
              hideTabBar={true}
            />
          )}
          {activeTab === 'audit' && isAdminOrOwner && (
            <AriaOrgSettings organization={organization} activeTab="audit" hideTabBar={true} />
          )}
          </>
        )}
      </div>
    </div>
  )
}
